import {
  CreateItemMutationVariables,
  ItemCreateContactTypeEnum
} from '../../graphql/graphql';

export type CategoryValues = {
  hasRooms: boolean;
  hasFloor: boolean;
  hasRepair: boolean;
  hasLandArea: boolean;
  hasBuildingType: boolean;
  areRoomsRequired: boolean;
  photosRequired: number;
  areaUnits: string;
};

export type Photo = { id: string; [key: string]: string };

export type ItemCreationFormValuesType = {
  area: string | number;
  categoryId: string;
  name: string;
  owner: string;
  email: string;
  isLeased: string;
  address: string;
  city_id: string;
  coordinates: { lat: number; lng: number };
  photos: Photo[];
  price: string | number;
  roomsAmount: string | number;
  buildingType?: string;
  description?: string;
  hasBillOfSale?: boolean;
  hasMortgage?: boolean;
  hasRepair?: string;
  paidDaily?: string;
  rooms?: string | number;
  townshipId?: string | string[];
  regionId?: string | string[];
  landmarkIds?: string[];
  landArea?: string | number;
  floor?: string | number;
  totalFloors?: string | number;
  categoryValues: CategoryValues;
  cityHasLocations: boolean;
  phone: string;
  typeEstate?: string;
};

const NOT_REQUIRED_FORM_FIELDS = {
  buildingType: 'default',
  description: 'default',
  hasBillOfSale: 'default',
  hasMortgage: 'default',
  hasRepair: 'stringBool',
  paidDaily: 'stringBool',
  rooms: 'number',
  townshipId: 'location',
  regionId: 'location',
  landmarkIds: 'landmark',
  landArea: 'area',
  floor: 'floorValue',
  totalFloors: 'floorTotal'
};

type FieldKey = keyof typeof NOT_REQUIRED_FORM_FIELDS;

const getRestItemParams = (
  values: ItemCreationFormValuesType,
  res: CreateItemMutationVariables
): CreateItemMutationVariables =>
  Object.entries(NOT_REQUIRED_FORM_FIELDS).reduce((acc, [k, value]) => {
    const key = k as FieldKey;
    if (values[key] === undefined) return acc;

    switch (value) {
      case 'stringBool':
        return {
          ...acc,
          [k]: values[key] === 'true'
        } as CreateItemMutationVariables;
      case 'number':
        return { ...acc, [k]: +values[key] } as CreateItemMutationVariables;
      case 'location':
        return {
          ...acc,
          location: { ...acc.location, [k]: values[key] }
        } as CreateItemMutationVariables;
      case 'landmark':
        return (values[key] as string[])?.length > 0
          ? ({
              ...acc,
              location: { ...acc.location, [k]: values[key] }
            } as CreateItemMutationVariables)
          : acc;
      case 'area':
        return {
          ...acc,
          area: { ...acc.area, land: +values[key] }
        } as CreateItemMutationVariables;
      case 'floorValue':
        return {
          ...acc,
          floor: { ...acc.floor, value: +values[key] }
        } as CreateItemMutationVariables;
      case 'floorTotal':
        return {
          ...acc,
          floor: { ...acc.floor, total: +values[key] }
        } as CreateItemMutationVariables;
      default:
        return { ...acc, [k]: values[key] } as CreateItemMutationVariables;
    }
  }, res);

export const convertFormToQueryInput = (
  values: ItemCreationFormValuesType
): CreateItemMutationVariables => {
  const res = {
    area: {
      value: Number(values.area)
    },
    categoryId: values.categoryId,
    contact: {
      name: values.name,
      type: values.owner as ItemCreateContactTypeEnum,
      email: values.email
    },
    isLeased: values.isLeased === 'true',
    location: {
      address: values.address,
      cityId: values.city_id,
      latitude: values.coordinates.lat,
      longitude: values.coordinates.lng
    },
    photoIds: values.photos.map(el => el.id),
    price: Number(values.price),
    rooms: Number(values.roomsAmount)
  };

  return getRestItemParams(values, res);
};
